import { ConfigItem } from "src/core/model/TrackerConfig";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { CacheStrategy } from "src/core/model/VariableCollectionMetadata";

import { BaseDataCollector } from "./BaseDataCollector";

interface CacheEntry {
  value: unknown;
  timestamp: number;
  ttl: number;
}

/**
 * Collector with TTL-based caching.
 * Mirrors Android CachedDataCollector.
 *
 * Each variable can have a different cache strategy (FAST: 10s, MEDIUM: 1min, SLOW: 5min, HEAVY: session).
 * If the cached value is still fresh, the supplier is not called.
 */
export abstract class CachedDataCollector extends BaseDataCollector {
  private cache = new Map<TrackerVariableType, CacheEntry>();

  /**
   * Collect with caching: only re-evaluate if cache has expired.
   */
  protected collectWithCache(
    data: Map<TrackerVariableType, unknown>,
    variableType: TrackerVariableType,
    strategy: CacheStrategy,
    supplier: () => unknown,
    configs?: ConfigItem[],
  ): void {
    this.collectIfNeeded(
      data,
      variableType,
      () => {
        const cached = this.cache.get(variableType);
        const now = Date.now();

        if (cached && now - cached.timestamp < cached.ttl) {
          return cached.value;
        }

        const value = supplier();
        this.cache.set(variableType, { value, timestamp: now, ttl: strategy });
        return value;
      },
      configs,
    );
  }

  /**
   * Invalidate a specific cached variable.
   */
  protected invalidateCache(variableType: TrackerVariableType): void {
    this.cache.delete(variableType);
  }

  /**
   * Clear all cached values.
   */
  protected clearCache(): void {
    this.cache.clear();
  }
}
